import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Student, Profile } from '@/lib/types';

/**
 * Hook do "auto-aluno": o personal trainer vê a si mesmo como aluno
 * pra reusar toda a infra de treino/progresso. Cria sob demanda se
 * ainda não existir.
 */
export function useSelfStudent(profile: Profile | null) {
  const queryClient = useQueryClient();
  const personalId = profile?.id ?? '';

  const { data: selfStudent, isLoading: loading } = useQuery({
    queryKey: ['self-student', personalId],
    queryFn: async (): Promise<Student | null> => {
      const { data, error } = await supabase
        .from('students')
        .select('*')
        .eq('personal_id', personalId)
        .eq('is_self', true)
        .maybeSingle();
      if (error) throw error;
      return (data as Student | null);
    },
    enabled: !!personalId,
  });

  const { mutateAsync: ensureSelfStudent, isPending: creating } = useMutation({
    mutationFn: async (): Promise<Student> => {
      if (selfStudent) return selfStudent;
      if (!profile) throw new Error('Sem profile');

      const { data, error } = await supabase
        .from('students')
        .insert({
          personal_id: profile.id,
          profile_id: profile.id,
          full_name: profile.full_name ?? null,
          email: profile.email,
          is_self: true,
          status: 'active',
          onboarding_completed: true,
        })
        .select()
        .single();
      if (error) throw error;
      return data as Student;
    },
    onSuccess: (s) => queryClient.setQueryData(['self-student', personalId], s),
  });

  return { selfStudent: selfStudent ?? null, loading, creating, ensureSelfStudent };
}
